import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface MyTeamModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

function MyTeamModal({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className = '',
}: MyTeamModalProps) {
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  const modalContent = (
    <div
      className="myteam-modal-overlay"
      onClick={handleOverlayClick}
      onKeyDown={(e) => e.key === 'Escape' && onOpenChange(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="myteam-modal-title"
    >
      <div
        className={`myteam-modal-content ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="myteam-modal-header">
          <h2 id="myteam-modal-title" className="myteam-modal-title">
            {title}
          </h2>
        </div>
        <div className="myteam-modal-body">{children}</div>
        {footer && <div className="myteam-modal-footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default MyTeamModal;
